import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import type { HeatmapOrchard, InspectionPoint } from '../common/ScoutingMap';
import {
  getDaysSinceDate,
  getFreshnessStatus,
  getFreshnessColor,
  FreshnessThresholds,
} from '../../utils/scoutingUtils';
import {
  computeScoutingKpis,
  computeOverdueOrchards,
  computeRecentPestDetections,
  computeInactiveScouters,
  SummaryEntry as MathSummaryEntry,
} from '../../utils/scoutingDashboardMath';
import { getDashboardSettings } from '../../services/dashboardSettingsService';
import PageHeader from '../shell/PageHeader';
import { useMenu } from '../../auth/useMenu';
import { ScoutingKpiRow } from './ScoutingKpiRow';
import { ScoutingMapBand } from './ScoutingMapBand';
import { ScoutingFreshnessCard, FreshnessRow } from './ScoutingFreshnessCard';
import { ScoutingAlertStack } from './ScoutingAlertStack';
import './ScoutingDashboard.css';

interface SummaryEntry extends MathSummaryEntry {
  // Narrow boundary/location to the shapes ScoutingMap expects.
  boundary: HeatmapOrchard['boundary'] | null;
  location: HeatmapOrchard['location'] | null;
  latestPoints: Omit<InspectionPoint, 'scouterName'>[];
}

interface PestRecord {
  code: string;
  name: string;
}

const ScoutingDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canReviewV2 = useMenu('scouting:reviewV2');
  // All users who reach ScoutingDashboard have scouting access; data-level
  // permission checks are handled by the backend. canViewRecords is always true.
  const canViewRecords = true;

  const [summary, setSummary] = useState<SummaryEntry[]>([]);
  const [thresholds, setThresholds] = useState<FreshnessThresholds>({
    greenDays: 3,
    yellowDays: 7,
    greenColor: '#27ae60',
    yellowColor: '#f39c12',
    redColor: '#e74c3c',
  });
  const [pestMap, setPestMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const fetchPests = async (): Promise<{ data: PestRecord[] }> => {
          try {
            const res = await axios.get<PestRecord[]>('/api/v1/pests', { headers });
            return res ?? { data: [] };
          } catch {
            return { data: [] };
          }
        };
        const [summaryRes, settings, pestsRes] = await Promise.all([
          axios.get<SummaryEntry[]>('/api/v1/scouting/summary', { headers }),
          getDashboardSettings(),
          fetchPests(),
        ]);
        setSummary(summaryRes.data);
        setThresholds({
          greenDays: settings.greenDays,
          yellowDays: settings.yellowDays,
          greenColor: settings.greenColor,
          yellowColor: settings.yellowColor,
          redColor: settings.redColor,
        });
        setPestMap(Object.fromEntries((pestsRes.data ?? []).map(p => [p.code, p.name])));
      } catch (err: any) {
        Sentry.captureException(err);
        setError(err.message ?? 'Failed to load scouting summary');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const heatmapOrchards = useMemo<HeatmapOrchard[]>(
    () =>
      summary
        .filter((s): s is SummaryEntry & { boundary: NonNullable<SummaryEntry['boundary']> } => !!s.boundary)
        .map(s => {
          const days = getDaysSinceDate(s.lastScoutedDate);
          const status = getFreshnessStatus(days, thresholds);
          return {
            _id: s.orchardId,
            blockId: s.blockId,
            orchardName: s.orchardName ?? s.orchardId,
            farmName: s.farmName ?? '',
            boundary: s.boundary,
            location: s.location ?? undefined,
            freshnessColor: getFreshnessColor(status, thresholds),
            lastScoutedDate: s.lastScoutedDate,
            lastScouterName: s.lastScouterName,
            latestRecordId: s.latestRecordId,
          };
        }),
    [summary, thresholds]
  );

  const allPoints = useMemo<InspectionPoint[]>(
    () =>
      summary.flatMap(s =>
        (s.latestPoints ?? []).map(p => ({
          ...(p as Omit<InspectionPoint, 'scouterName'>),
          scouterName: s.lastScouterName ?? undefined,
        }))
      ),
    [summary]
  );

  const kpis = useMemo(() => computeScoutingKpis(summary, thresholds), [summary, thresholds]);
  const overdue = useMemo(() => computeOverdueOrchards(summary, thresholds, 5), [summary, thresholds]);
  const detections = useMemo(() => computeRecentPestDetections(summary, 14, 5), [summary]);
  const inactive = useMemo(() => computeInactiveScouters(summary, 14), [summary]);

  const freshnessRows = useMemo<FreshnessRow[]>(
    () =>
      summary.map(s => ({
        orchardId: s.orchardId,
        orchardName: s.orchardName ?? s.orchardId,
        farmName: s.farmName ?? '',
        lastScoutedDate: s.lastScoutedDate,
        lastScouterName: s.lastScouterName,
      })),
    [summary]
  );

  if (loading) {
    return (
      <div
        role="status"
        aria-label="Loading scouting dashboard"
        style={{ padding: '24px', textAlign: 'center' }}
      >
        {t('dashboard.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        style={{ padding: '16px', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: 6 }}
      >
        {error}
      </div>
    );
  }

  return (
    <div data-testid="scouting-dashboard" className="scouting-dashboard">
      <PageHeader
        title={t('nav.scouting')}
        meta={t('dashboard.scouting.findingsToday', { count: detections.length })}
        actions={
          <>
            {canReviewV2 && (
              <button
                type="button"
                className="pat-btn pat-btn--subtle"
                data-testid="overview-matrix-link"
                onClick={() => navigate('/scouting/review/matrix')}
              >
                {t('fieldScout.review.summary.openMatrix')}
              </button>
            )}
            <button
              type="button"
              className="pat-btn pat-btn--primary"
              onClick={() => navigate('/scouting/field-v2')}
            >
              {t('dashboard.scouting.actions.fieldMode')}
            </button>
          </>
        }
      />
      <ScoutingKpiRow kpis={kpis} />
      <ScoutingMapBand
        heatmapOrchards={heatmapOrchards}
        points={allPoints}
        thresholds={thresholds}
        canViewRecords={canViewRecords}
      />
      <div className="scouting-dashboard__body">
        <div className="scouting-dashboard__body-left">
          <ScoutingFreshnessCard
            rows={freshnessRows}
            thresholds={thresholds}
            viewAllHref="/scouting/review"
          />
        </div>
        <div className="scouting-dashboard__body-right">
          <ScoutingAlertStack
            overdue={overdue}
            detections={detections}
            inactive={inactive}
            pestMap={pestMap}
          />
        </div>
      </div>
    </div>
  );
};

export { ScoutingDashboard };
